import React, { useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Modal, Spin } from 'antd';
import { SolgariApiService } from '@/services/solgari-api/solgari-api.service';
import { IAgentApi } from '@/services/solgari-api/interfaces/agent.interface';
import { Agent } from './Agent';
import { getExtensionFromUri } from '@/shared/helpers/get-from-uri.helper';
import { observer } from 'mobx-react-lite';
import { useStore } from '@/components/../stores';
import { openNotification } from '@/services/utils/open-notification.util';
import { AxiosError } from 'axios';
import { EPresenceStatusApi } from '@/shared/enums/presence.enum';
import { SipJs } from '@/services/sip-js.service';
import './ChatTransfer.scss';

const { Search } = Input;

type Props = {
  showTransferModal: boolean;
  onChatTransferModalClose: () => void;
};

const SortPriority = {
  [EPresenceStatusApi.AVAILABLE]: 1,
  [EPresenceStatusApi.BUSY]: 2,
  [EPresenceStatusApi.NOT_DISTURB]: 3,
  [EPresenceStatusApi.ON_A_CALL]: 4,
  [EPresenceStatusApi.BREAK]: 5,
  [EPresenceStatusApi.WRAP_UP]: 6,
  [EPresenceStatusApi.AWAY]: 7,
  [EPresenceStatusApi.BERIGHTBACK]: 8,
  [EPresenceStatusApi.OFFLINE]: 11,
  default: 12,
};

export const ChatTransfer = observer(
  ({ showTransferModal, onChatTransferModalClose }: Props) => {
    const persistedAgents = useRef<IAgentApi[]>(null);
    const [agents, setAgents] = useState<IAgentApi[]>([]);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [isTransferring, setTransfer] = useState<boolean>(false);

    const {
      chatsStore: { chatDetails, getChatsFromApi },
    } = useStore();
    const navigate = useNavigate();

    const onTransfer = async (agentEmail: string) => {
      try {
        setTransfer(true);
        await SolgariApiService.transferChat(agentEmail, chatDetails.chatId);
        await getChatsFromApi();
        navigate('/inbox');
      } catch (e) {
        const axiosError = e as AxiosError<{ error: string }>;
        const { error } = axiosError.response.data;
        openNotification({
          onClick: () => null,
          onClose: undefined,
          type: 'error',
          text: `${error}`,
          durationSec: 5,
        });
      } finally {
        setTransfer(false);
      }
    };

    const sortAgentsByStatus = (agents: IAgentApi[]) => {
      return agents.sort((a, b) => {
        const priorityA = SortPriority[a.presence] || SortPriority.default;
        const priorityB = SortPriority[b.presence] || SortPriority.default;
        return priorityA - priorityB;
      });
    };

    const getAgentsFromApi = async (): Promise<void> => {
      setLoading(true);
      const listOfAgents = await SolgariApiService.getListOfAgents();
      const ownExtensionId = getExtensionFromUri(SipJs.authData.uri);
      const filteredAgents = listOfAgents.filter(
        (agent: IAgentApi) => String(agent.extensionId) !== ownExtensionId,
      );
      const sortedAgents = sortAgentsByStatus(filteredAgents);
      persistedAgents.current = sortedAgents;
      setAgents(sortedAgents);
      setLoading(false);
    };

    useEffect(() => {
      showTransferModal && getAgentsFromApi();
    }, [showTransferModal]);

    const onSearch = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      const searchValue = value.trim().toLowerCase();
      const filteredAgents = persistedAgents.current.filter((a) =>
        `${a.fullName}`.toLowerCase().includes(searchValue),
      );
      setAgents(filteredAgents);
    }, 500);

    return (
      <Modal
        closable
        destroyOnClose
        title="Chat Transfer"
        open={showTransferModal}
        onCancel={onChatTransferModalClose}
        className="transfer-modal"
        footer={[
          <Button key="back" onClick={onChatTransferModalClose}>
            Cancel
          </Button>,
        ]}
      >
        <div className="chat-transfer-wrapper">
          <Search
            className="chat-transfer-search"
            placeholder="Search"
            allowClear
            onChange={onSearch}
          />
          <div className="chat-transfer-body">
            {isLoading ? (
              <Spin className="spinner" />
            ) : (
              agents.map((agent) => (
                <Agent
                  key={agent.extensionId}
                  presenceStatus={agent.presence}
                  email={agent.email}
                  userName={agent.fullName}
                  isTransferring={isTransferring}
                  onTransfer={onTransfer}
                />
              ))
            )}
          </div>
        </div>
      </Modal>
    );
  },
);
